import { useSearchStore } from '@stores/search.store'

import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'

import dynamic from 'next/dynamic'
import Image from 'next/image'

import Button from '@components/buttons/button'
import { ItemType } from '@components/inputs/dropdown'
import { DropDownProps } from '@components/inputs/dropdown/dropdown'
import useSearchDispatcher from '@hooks/useSearchDispatcher'

import Amount from './amount'
import filtersIcon from './sliders-horizontal.svg'
import SortValue from './sort-value'

import styles from './search-results-header.module.scss'

const DropDown = dynamic(() => import('@components/inputs/dropdown')) as ComponentType<DropDownProps<SortValue>>

type SearchResultsHeaderProps = {
  onFiltersButtonClick: () => void
  paginationInfo?: {
    page: number
    total: number
  }
  location?: string
}

const SearchResultsHeader = ({
  onFiltersButtonClick,
  location,
  paginationInfo,
}: SearchResultsHeaderProps): JSX.Element => {
  const searchDispatcher = useSearchDispatcher()
  const search = useSearchStore((state) => state.search)

  const options = useMemo(
    () => [
      { value: SortValue.FEATURED, label: 'Most Relevant' },
      { value: SortValue.MOST_RECENT, label: 'Most Recent' },
      { value: SortValue.NEAREST, label: 'Nearest' },
    ],
    [],
  )

  const getSortByKey = useCallback((k: string) => options.find((x) => x.value === k), [options])

  const [sortValue, setSortValue] = useState<ItemType<SortValue> | undefined>(getSortByKey(search.sort))

  useEffect(() => {
    setSortValue(getSortByKey(search.sort))
  }, [search.sort, getSortByKey])

  return (
    <div className={styles['search-results-header']}>
      <div className={styles.dropdown}>
        <DropDown
          ariaLabel="Sort"
          id={`sortSelect${location ? `-${location}` : ''}`}
          options={options}
          initialValue={sortValue || options[0]}
          onChange={({ value }): void => searchDispatcher({ sort: value })}
          key={sortValue?.label}
        />
      </div>
      <Button variant="gray" onClick={onFiltersButtonClick} className={styles['mobile-filters-button']}>
        <Image src={filtersIcon} height={24} width={24} alt="filters icon" />
        Refine
      </Button>
      {paginationInfo && <Amount page={paginationInfo.page} total={paginationInfo.total} className={styles.amount} />}
    </div>
  )
}

export default SearchResultsHeader
